import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirmation } from '../../hooks/useModals';
import { adminService } from '../../services';
import Input from '../../components/ui/Input';
import { LoadingSpinner } from '../../components/ui/Loading';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import EditUserModal from '../../components/modals/EditUserModal';
import { ChevronLeft, Edit, Power } from 'lucide-react';
import { isAdminEquivalent, PRIVILEGED_ROLES, getRoleLabel } from '../../utils/permissions';

const AdminUsers = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const { modalState: confirmState, confirm, close: closeConfirm } = useConfirmation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Input separado para busca
  const [statusFilter, setStatusFilter] = useState(''); // Filtro de status (ativo/inativo)
  const [roleFilter, setRoleFilter] = useState(''); // Filtro de role (admin/member)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const itemsPerPage = 12;

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Remove parâmetros undefined
      const params = {};
      if (currentPage) params.page = currentPage;
      if (itemsPerPage) params.limit = itemsPerPage;
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role = roleFilter;

      const response = await adminService.getAllUsers(params);
      
      setUsers(response.users || response.data || []);
      setTotalPages(response.pagination?.pages || response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.total || 0);
    } catch (error) {      
      if (error.response?.status === 401) {
        setError('Você não está autenticado. Faça login novamente.');
      } else if (error.response?.status === 403) {
        setError('Você não tem permissão para acessar esta página.');
      } else {
        setError(error.response?.data?.error || 'Erro ao carregar usuários. Tente novamente.');
      }
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, roleFilter]);

  useEffect(() => {
    if (isAdminEquivalent(user?.role)) {
      loadUsers();
    }
  }, [user, loadUsers]);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setRoleFilter('');
    setCurrentPage(1);
  };

  const handleRoleChange = async (userId, newRole) => {
    const confirmed = await confirm({
      title: 'Alterar Papel do Usuário',
      message: `Tem certeza que deseja alterar o papel do usuário para ${newRole}?`,
      confirmText: 'Alterar',
      cancelText: 'Cancelar',
      type: 'warning',
      icon: '👤'
    });

    if (!confirmed) return;

    try {
      await adminService.updateUserRole(userId, { role: newRole });
      notify.success('Papel do usuário alterado com sucesso!', 'Usuário Atualizado');
      await loadUsers();
    } catch (error) {
      console.error('Erro ao alterar papel:', error);
      notify.error('Erro ao alterar papel do usuário. Tente novamente.', 'Erro');
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    const action = currentActive ? 'desativar' : 'ativar';
    const actionPast = currentActive ? 'desativado' : 'ativado';
    const confirmed = await confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Usuário`,
      message: `Tem certeza que deseja ${action} este usuário? ${!currentActive ? 'O usuário poderá fazer login.' : 'O usuário não poderá fazer login.'}`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
      type: 'warning',
      icon: '⚡'
    });

    if (!confirmed) return;

    try {
      await adminService.toggleUserActive(userId);
      notify.success(`Usuário ${actionPast} com sucesso!`, 'Status Alterado');
      await loadUsers();
    } catch (error) {
      console.error('Erro ao alternar status:', error);
      notify.error(`Erro ao ${action} usuário. Tente novamente.`, 'Erro');
    }
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditModalOpen(true);
  };

  const handleSaveUser = async (userId, userData) => {
    try {
      await adminService.updateUser(userId, userData);
      notify.success('Usuário atualizado com sucesso!', 'Salvo');
      await loadUsers();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar usuário');
    }
  };

  if (!isAdminEquivalent(user?.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-8 border border-red-500/20 shadow-xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-5xl">🚫</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Acesso Negado
          </h2>
          <p className="text-gray-400 mb-6">
            Apenas administradores podem acessar esta página.
          </p>
          <button
            onClick={() => window.location.href = '/app/dashboard'}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.location.href = '/app/admin'}
                className="flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-1">
                  👥 Gerenciar Usuários
                </h1>
                <p className="text-gray-300">{totalItems > 0 ? `${totalItems} usuários encontrados` : 'Gerencie os membros do clã'}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Filtros */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <h3 className="text-lg font-semibold text-white mb-4">🔍 Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Buscar Usuários
              </label>
              <Input
                type="text"
                placeholder="Nome ou email... (Enter)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
              >
                <option value="" className="bg-slate-800">Todos</option>
                <option value="APPROVED" className="bg-slate-800">Ativos</option>
                <option value="PENDING" className="bg-slate-800">Pendentes</option>
                <option value="REJECTED" className="bg-slate-800">Rejeitados</option>
                <option value="SUSPENDED" className="bg-slate-800">Suspensos</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Papel
              </label>
              <select
                value={roleFilter}
                onChange={(e) => handleRoleFilterChange(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
              >
                <option value="" className="bg-slate-800">Todos</option>
                <option value="ADMIN" className="bg-slate-800">Administradores</option>
                <option value="RECRUITER" className="bg-slate-800">Recrutadores</option>
                <option value="MEMBER" className="bg-slate-800">Membros</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl text-white transition-all duration-300"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-6 backdrop-blur-sm">
            <div className="flex items-center">
              <span className="text-red-400 text-3xl mr-4">⚠️</span>
              <div className="flex-1">
                <h3 className="text-red-400 font-semibold mb-1">Erro ao carregar usuários</h3>
                <p className="text-red-300 text-sm">{error}</p>
              </div>
              <button
                onClick={loadUsers}
                className="ml-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg text-red-400 hover:text-red-300 transition-all duration-300"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {/* Lista de Usuários */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {users.length === 0 ? (
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-12 border border-white/10 shadow-xl text-center">
                <div className="text-6xl mb-4">👥</div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Nenhum usuário encontrado
                </h3>
                <p className="text-gray-400">
                  {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Nenhum usuário cadastrado no sistema.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {users.map((userItem) => (
                  <div 
                    key={userItem.id}
                    className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl"
                  >
                    {/* Header do Usuário */}
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                        👤
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-base truncate">
                          {userItem.name}
                        </h3>
                        <p className="text-sm text-gray-400 truncate">
                          {userItem.email}
                        </p>
                      </div>
                    </div>

                    {/* Papel e Status */}
                    <div className="mb-4 flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold ${
                        userItem.role === 'SUPER_ADMIN'
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : userItem.role === 'TESOUREIRO'
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : userItem.role === 'ADMIN'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : userItem.role === 'RECRUITER'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      }`}>
                        {getRoleLabel(userItem.role)}
                      </span>
                      
                      <button
                        onClick={() => handleToggleActive(userItem.id, userItem.active ?? true)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 ${
                          (userItem.active ?? true)
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30' 
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30 hover:bg-gray-500/30'
                        }`}
                        title={(userItem.active ?? true) ? 'Usuário ativo' : 'Usuário inativo'}
                      >
                        <Power size={12} />
                        {(userItem.active ?? true) ? 'Ativo' : 'Inativo'}
                      </button>
                    </div>

                    {/* Data de criação */}
                    <div className="mb-4 pb-4 border-b border-white/10">
                      <p className="text-xs text-gray-400">
                        📅 Membro desde: {new Date(userItem.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>

                    {/* Ações */}
                    <div className="space-y-2">
                      {/* Botão de Editar */}
                      <button
                        onClick={() => handleEditUser(userItem)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 hover:border-blue-500/50 rounded-lg text-blue-400 hover:text-blue-300 transition-all duration-300 text-sm font-medium"
                      >
                        <Edit size={16} />
                        Editar Dados
                      </button>

                      {/* Alterar Papel (apenas se não for o próprio usuário) */}
                      {userItem.id !== user.id ? (
                        <div>
                          <label className="block text-xs text-gray-400 mb-1.5">Alterar Papel:</label>
                          <select
                            value={userItem.role}
                            onChange={(e) => handleRoleChange(userItem.id, e.target.value)}
                            disabled={user?.role !== 'SUPER_ADMIN' && PRIVILEGED_ROLES.includes(userItem.role)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 hover:border-purple-500/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="MEMBER" className="bg-slate-800">Membro</option>
                            <option value="RECRUITER" className="bg-slate-800">Recrutador</option>
                            <option value="ADMIN" className="bg-slate-800">Administrador</option>
                            <option value="TESOUREIRO" className="bg-slate-800" disabled={user?.role !== 'SUPER_ADMIN'}>Tesoureiro</option>
                            <option value="SUPER_ADMIN" className="bg-slate-800" disabled={user?.role !== 'SUPER_ADMIN'}>Super Admin</option>
                          </select>
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          <span className="text-xs text-gray-500 italic">Você não pode alterar seu próprio papel</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Informações */}
                  <div className="text-sm text-gray-300">
                    Mostrando <span className="font-bold text-cyan-400">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span> a <span className="font-bold text-cyan-400">{Math.min(currentPage * itemsPerPage, totalItems)}</span> de <span className="font-bold text-cyan-400">{totalItems}</span> usuários
                  </div>

                  {/* Controles de Paginação */}
                  <div className="flex items-center gap-2">
                    {/* Primeira Página */}
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
                      title="Primeira página"
                    >
                      ⏮️
                    </button>

                    {/* Página Anterior */}
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
                    >
                      ← Anterior
                    </button>

                    {/* Números de Página */}
                    <div className="hidden sm:flex items-center gap-1">
                      {(() => {
                        const pages = [];
                        const showPages = 5; // Quantas páginas mostrar
                        let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                        let endPage = Math.min(totalPages, startPage + showPages - 1);

                        // Ajustar se estiver no final
                        if (endPage - startPage < showPages - 1) {
                          startPage = Math.max(1, endPage - showPages + 1);
                        }

                        // Primeira página se não estiver visível
                        if (startPage > 1) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => setCurrentPage(1)}
                              className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
                            >
                              1
                            </button>
                          );
                          if (startPage > 2) {
                            pages.push(
                              <span key="ellipsis1" className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }
                        }

                        // Páginas do meio
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => setCurrentPage(i)}
                              className={`px-3 py-2 border rounded-lg transition-all duration-300 ${
                                i === currentPage
                                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 border-cyan-500 text-white font-bold shadow-lg shadow-cyan-500/50'
                                  : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-cyan-500/50 text-gray-300 hover:text-white'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }

                        // Última página se não estiver visível
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="ellipsis2" className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                              className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                    </div>

                    {/* Indicador mobile */}
                    <div className="sm:hidden px-3 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-lg">
                      <span className="font-bold text-white">{currentPage}</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-gray-300">{totalPages}</span>
                    </div>

                    {/* Próxima Página */}
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
                    >
                      Próxima →
                    </button>

                    {/* Última Página */}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
                      title="Última página"
                    >
                      ⏭️
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
          )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          onClose={closeConfirm}
          onConfirm={confirmState.onConfirm}
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          type={confirmState.type}
          icon={confirmState.icon}
        />

        {/* Edit User Modal */}
        <EditUserModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSave={handleSaveUser}
        />
      </div>
    </div>
  );
};

export default AdminUsers;